import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLogOutMutation } from 'src/services/auth';
import { useLazyMyProfileQuery } from 'src/services/user';
import { theme } from '../../services/theme';
import { ChangePasswordModal } from '../ChangePasswordModal';
import {
  AccountIcon,
  BackIcon,
  ChangePasswordIcon,
  EmailIcon,
  LogOutIcon,
  OfficeIcon,
  UserIcon,
} from '../icons';
import { ProfileOption } from './ProfileOption';

const emptyFieldValue = '--';

export const MyProfileMenu = () => {
  const [logOut] = useLogOutMutation();
  const onLogOut = useCallback(async () => {
    await logOut();
  }, [logOut]);

  const [isMenuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const [isChangePasswordModalOpen, setChangePasswordModalOpen] =
    useState(false);

  const [fetchMyProfile, { data }] = useLazyMyProfileQuery();

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const options = useMemo(
    () =>
      [
        {
          icon: (
            <UserIcon
              sx={{
                color: '#9AB1C0',
                fontSize: '20px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '17px',
                },
              }}
            />
          ),
          name: 'First name',
          value: `${data?.firstName || ''}`.trim() || emptyFieldValue,
        },
        {
          icon: (
            <UserIcon
              sx={{
                color: '#9AB1C0',
                fontSize: '20px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '17px',
                },
              }}
            />
          ),
          name: 'Last name',
          value: `${data?.lastName || ''}`.trim() || emptyFieldValue,
        },
        {
          icon: (
            <OfficeIcon
              sx={{
                color: '#9AB1C0',
                fontSize: '20px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '17px',
                },
              }}
            />
          ),
          name: 'Employer',
          value: data?.firm?.name || emptyFieldValue,
        },
        {
          icon: (
            <EmailIcon
              sx={{
                color: '#9AB1C0',
                fontSize: '20px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '17px',
                },
              }}
            />
          ),
          name: 'Email',
          value: data?.email || emptyFieldValue,
        },
      ] as Array<Parameters<typeof ProfileOption>[0]>,
    [data],
  );

  return (
    <>
      <AccountIcon
        onClick={toggleMenu}
        stroke="#9AB1C0"
        sx={{
          color: '#9AB1C0',
          cursor: 'pointer',
          fontSize: '42px',
          [theme.breakpoints.down('sm')]: {
            fontSize: '38px',
          },
          [theme.breakpoints.down('mobile')]: {
            fontSize: '36px',
          },
        }}
      />
      <Dialog
        BackdropProps={{ sx: { backgroundColor: 'transparent' } }}
        PaperProps={{
          sx: {
            borderRadius: '0px 0px 0px 20px',
            boxShadow: '-1px 1px 2px rgba(0, 0, 0, 0.25)',
            height: '428px',
            m: 0,
            minHeight: '428px',
            position: 'fixed',
            right: 0,
            top: 0,
            width: '474px',
            [theme.breakpoints.down('sm')]: {
              width: '300px',
              minHeight: '410px',
              height: '410px',
            },
          },
        }}
        onBackdropClick={toggleMenu}
        open={isMenuOpen}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '20px',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
              padding: '10px',
            },
          }}
        >
          <BackIcon
            onClick={toggleMenu}
            stroke="#9AB1C0"
            sx={{ cursor: 'pointer', fontSize: '16px', marginRight: '21px' }}
          />
          <Typography
            fontWeight="regular"
            sx={{
              color: '#260F26',
              fontSize: '20px',
              [theme.breakpoints.down('sm')]: {
                fontSize: '16px',
              },
            }}
          >
            My profile
          </Typography>
          <Typography
            onClick={onLogOut}
            sx={{
              cursor: 'pointer',
              color: '#356482',
              fontSize: '14px',
              fontWeight: '600',
            }}
          >
            Log out
          </Typography>
        </Box>
        <List
          sx={{
            m: 0,
            padding: '8px 48px',
            [theme.breakpoints.down('sm')]: {
              padding: '8px 20px',
            },
            [theme.breakpoints.down('mobile')]: {
              padding: '5px 6px',
            },
          }}
        >
          {options.map((opt) => (
            <ProfileOption key={opt.name} {...opt} />
          ))}
          <Link
            component={Box}
            onClick={() => {
              setMenuOpen(false);
              setChangePasswordModalOpen(true);
            }}
            style={{
              alignItems: 'center',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: '30px',
              width: '100%',
              [theme.breakpoints.down('sm')]: {
                marginTop: '15px',
              },
            }}
          >
            <ChangePasswordIcon sx={{ color: '#356482', fontSize: '20px' }} />
            <Typography
              fontWeight="regular"
              sx={{
                color: '#356482',
                fontSize: '14px',
                marginLeft: '15px',
                textDecorationLine: 'underline',
              }}
            >
              Change password
            </Typography>
          </Link>
        </List>
      </Dialog>
      <ChangePasswordModal
        adminReset={false}
        isOpen={isChangePasswordModalOpen}
        onClose={() => {
          setChangePasswordModalOpen(false);
        }}
      />
    </>
  );
};
